import {
  afaireFocusHref,
  consequencesSummary,
  isNewForAssignee,
  type Plan,
  type PlanRun,
  type StepResult,
} from '@cockpit/shared';

/** Logique pure de l'écran de validation (testée sans navigateur). */

export interface StepDraft {
  key: string;
  checked: boolean;
  choice?: string;
  edits: Record<string, string>;
}

/**
 * Valeur affichée d'un champ modifiable : le texte tel quel, une liste de textes (destinataires d'un
 * brouillon) jointe par des virgules ; sinon vide.
 */
export function editableText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.every((v) => typeof v === 'string')) return value.join(', ');
  return '';
}

/** État initial des lignes : cases selon `defaultChecked`, premier choix, champs pré-remplis. */
export function initialDrafts(plan: Plan): StepDraft[] {
  return plan.steps.map((step) => ({
    key: step.key,
    checked: step.defaultChecked,
    ...(step.choices?.[0] ? { choice: step.choices[0].value } : {}),
    edits: Object.fromEntries((step.editable ?? []).map((e) => [e.field, editableText(step.input[e.field])])),
  }));
}

/** Corps de `POST /api/plans/:id/execute` : seules les modifications réelles sont envoyées. */
export function executeBody(plan: Plan, drafts: StepDraft[]) {
  return {
    steps: drafts.map((draft) => {
      const step = plan.steps.find((s) => s.key === draft.key);
      // Un champ non touché garde la valeur du serveur (ex. la liste des destinataires d'un brouillon).
      const edits = Object.fromEntries(
        Object.entries(draft.edits).filter(([field, value]) => editableText(step?.input[field]) !== value),
      );
      return {
        key: draft.key,
        checked: draft.checked,
        ...(draft.checked && draft.choice !== undefined ? { choice: draft.choice } : {}),
        ...(draft.checked && Object.keys(edits).length > 0 ? { edits } : {}),
      };
    }),
  };
}

export const RESULT_LABELS: Record<StepResult['status'], string> = {
  ok: 'fait',
  error: 'erreur',
  skipped: 'sauté',
};
export const RESULT_CLASSES: Record<StepResult['status'], string> = {
  ok: 'c-ok',
  error: 'c-bad',
  skipped: 'c-mut',
};

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''}`;

/** Message du toast après exécution (comme la maquette : « 2 opérations exécutées, message envoyé en privé »). */
export function summarizeRun(run: PlanRun, drafts: StepDraft[]): string {
  const checked = new Set(drafts.filter((d) => d.checked).map((d) => d.key));
  const ok = run.results.filter((r) => r.status === 'ok');
  const errors = run.results.filter((r) => r.status === 'error').length;
  const skipped = run.results.filter((r) => r.status === 'skipped' && checked.has(r.key)).length;

  let text =
    ok.length > 0
      ? `${plural(ok.length, 'opération')} exécutée${ok.length > 1 ? 's' : ''}`
      : 'Aucune opération exécutée';
  const chosen = ok
    .map((r) => {
      const step = run.plan.steps.find((s) => s.key === r.key);
      const draft = drafts.find((d) => d.key === r.key);
      return step?.choices?.find((c) => c.value === draft?.choice)?.label;
    })
    .find((label) => label !== undefined);
  if (chosen) text += `, message envoyé ${chosen}`;
  const extras = [
    skipped > 0 ? `${plural(skipped, 'étape')} sautée${skipped > 1 ? 's' : ''}` : '',
    errors > 0 ? `${errors} en erreur` : '',
  ].filter(Boolean);
  if (extras.length > 0) text += ` (${extras.join(', ')})`;
  return `${text}.`;
}

/**
 * Lot T : toast après exécution, avec le résumé des conséquences (« 1 opération exécutée. 1 carte ajoutée
 * pour Stéphane, 1 fermée. »). `base` : le message propre à l'écran (sinon `summarizeRun`).
 */
export function runToastText(run: PlanRun, drafts: StepDraft[], meId: string | null, base?: string): string {
  const text = base ?? summarizeRun(run, drafts);
  const consequences = consequencesSummary(run.consequences ?? [], meId);
  if (!consequences) return text;
  const head = text.endsWith('.') ? text : `${text}.`;
  return `${head} ${consequences.charAt(0).toUpperCase()}${consequences.slice(1)}.`;
}

/**
 * Lot T : lien « Voir dans À faire » du toast d'une action directe (pas d'écran de résultats) : les cartes
 * ajoutées d'abord, sinon les fermées ; null sans conséquence.
 */
export function consequencesLink(run: PlanRun): { label: string; to: string } | null {
  const consequences = run.consequences ?? [];
  if (consequences.length === 0) return null;
  const added = consequences.filter((c) => isNewForAssignee(c.kind));
  const ids = (added.length > 0 ? added : consequences).map((c) => c.actionId);
  return { label: 'Voir dans À faire', to: afaireFocusHref(ids) };
}

// ------------------------------------------------------------------ Lot BI : « Annuler » pendant 5 secondes

export const UNDONE_TOAST = "Annulé : rien n'est parti.";
export const TOO_LATE_TOAST = "Trop tard : l'action est déjà partie.";

/** Statuts d'un plan dont l'exécution est finie (résultats lisibles). */
export function isFinishedStatus(status: Plan['status']): boolean {
  return status === 'DONE' || status === 'PARTIAL' || status === 'FAILED';
}

/**
 * Exécution telle qu'elle se passera si tout réussit (étapes cochées « faites », sautées si une étape dont elles
 * dépendent n'est pas cochée) : sert au texte du toast affiché pendant le délai d'« Annuler », comme Gmail
 * affiche « Message envoyé » avant d'envoyer.
 */
export function predictedRun(run: PlanRun, drafts: StepDraft[]): PlanRun {
  const checked = new Set(drafts.filter((d) => d.checked).map((d) => d.key));
  const ok = new Set<string>();
  const results: StepResult[] = run.plan.steps.map((step) => {
    if (!checked.has(step.key)) return { key: step.key, status: 'skipped', message: 'Non cochée.' };
    if ((step.dependsOn ?? []).some((dep) => !ok.has(dep))) return { key: step.key, status: 'skipped' };
    ok.add(step.key);
    return { key: step.key, status: 'ok' };
  });
  return { plan: { ...run.plan, status: 'DONE' }, results };
}

/** Lot AT : une étape faite rend un lien à ouvrir (« Ouvrir le devis dans Axonaut »). */
export function runHasResultLink(run: PlanRun): boolean {
  return run.results.some((r) => r.status === 'ok' && !!(r.output as { link?: unknown } | undefined)?.link);
}
